// src/Pages/About.js
import React from "react";
import { Download, MapPin } from "lucide-react";
import bwGhib from "../assets/bw_ghib.png";

export default function About() {
  return (
    <div className="bg-slate-900 min-h-screen pb-20">
      {/* Top section: avatar, headline, resume button */}
      <section className="max-w-2xl mx-auto flex flex-col items-center text-center pt-12">
        <div className="w-40 h-40 rounded-full overflow-hidden shadow-lg mb-6 border-4 border-cyan-400">
          <img
            src={bwGhib}
            alt="Brian Williams"
            className="object-cover w-full h-full"
          />
        </div>

        <h1 className="text-5xl md:text-6xl font-extrabold text-slate-100 mb-4">About Me</h1>
        <p className="text-lg text-slate-400 max-w-2xl mb-8">
          I’m a calm, pragmatic security practitioner. I focus on turning noisy data
          into clear decisions, writing things down so others can repeat them, and
          shipping improvements that stack up.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4 mb-10">
          <a
            href="/Brian_Williams_Resume_w.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold px-7 py-3 rounded-full flex items-center gap-2 shadow-lg hover:scale-105 transition"
          >
            <Download className="w-5 h-5" /> Download Resume
          </a>
          <span className="flex items-center gap-2 text-slate-400 text-base">
            <MapPin className="w-5 h-5" /> Available worldwide (Remote)
          </span>
        </div>
      </section>

      {/* Stats bar */}
      <section className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 mb-14">
        <Stat number="15+" label="Years in Cybersecurity" />
        <Stat number="100+" label="Detections Authored" />
        <Stat number="30+" label="Automations Built" />
      </section>

      {/* My Journey (kept layout; refreshed copy) */}
      <section className="max-w-5xl mx-auto px-4 mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-100 mb-3">Focus & Philosophy</h2>
        <p className="text-center text-lg text-slate-400 mb-14">
          Straightforward methods, transparent trade-offs, solid outcomes.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">What I’m Like to Work With</h3>
            <p className="text-slate-400 mb-6">
              I’m practical and steady under pressure. I prefer simple solutions that others
              can run without me, and I document enough that the next person moves faster.
              I’m comfortable moving between analysis and <strong>building tools and automation</strong>,
              and I spend a lot of time modeling threats into behaviors that can actually be
              tested, tuned, and turned into custom detections. I keep a close eye on
              signal-to-noise so time is spent where it matters.
            </p>

            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl p-6 text-white shadow-lg">
              <div className="text-lg font-semibold mb-2">Strengths at a Glance</div>
              <div className="flex flex-col md:flex-row gap-6 text-sm">
                <div>
                  <span className="font-bold">Analysis</span>
                  <ul className="list-disc ml-6 mt-1">
                    <li>Hypothesis-driven investigations</li>
                    <li>Separating noise from true signals</li>
                    <li>Clear incident narratives</li>
                  </ul>
                </div>
                <div>
                  <span className="font-bold">Engineering</span>
                  <ul className="list-disc ml-6 mt-1">
                    <li>Small tools & automation when useful</li>
                    <li>Repeatable labs and reproducible steps</li>
                    <li>Readable, documented solutions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl p-6 text-white shadow-lg mb-6">
              <div className="text-lg font-semibold mb-2">Core Principles</div>
              <ul className="list-disc ml-6">
                <li>Clarity over cleverness</li>
                <li>Automate the repeatable; explain the rest</li>
                <li>Bias to small, safe, continuous delivery</li>
                <li>Assume breach; validate with data</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-xl p-6 text-white shadow-lg mb-6">
              <div className="text-lg font-semibold mb-2">Threat Hunting</div>
              <p className="text-sm leading-7 text-cyan-50 mb-4">
                My strongest hunts start with adversary behavior, not keyword chasing. I build
                hunt ideas from ATT&amp;CK-aligned TTPs, threat intelligence, telemetry gaps, and
                abnormal network patterns, then work them through Splunk, Zeek, PCAP, and related
                data sources until they become something operationally useful.
              </p>
              <ul className="list-disc ml-6 text-sm space-y-1">
                <li>Translate intelligence and attacker tradecraft into testable hunt hypotheses</li>
                <li>Use PCAP, Zeek, and Splunk together to validate behavior across the full workflow</li>
                <li>Turn successful hunts into durable custom detections, triage steps, and analyst guidance</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-white mb-2">Today &amp; What’s Next</h3>
            <p className="text-slate-400">
              I enjoy roles where I can improve signal quality, design durable detections,
              and build solutions that help teams investigate faster. A lot of my best work
              sits in the middle ground between threat hunting, threat modeling, and detection
              engineering: breaking activity into observable behaviors, building custom detections
              around those behaviors, and then validating them with repeatable lab traffic and
              real packet evidence. I like quiet competence, good docs, and work that makes others’
              jobs easier.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// Stat bar helper component
function Stat({ number, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-cyan-400 text-3xl md:text-4xl font-extrabold mb-1">{number}</span>
      <span className="text-base text-slate-300 font-semibold">{label}</span>
    </div>
  );
}
